import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { GameProvider } from "@/context/GameContext";
import CRTScreen from "@/components/CRTScreen";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Pages
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import Dashboard from "@/pages/Dashboard";
import OnlineQueue from "@/pages/OnlineQueue";
import OnlineLobby from "@/pages/OnlineLobby";
import NewGameSetup from "@/pages/NewGameSetup";
import CoinToss from "@/pages/CoinToss";
import GamePlay from "@/pages/GamePlay";
import InningSummary from "@/pages/InningSummary";
import EndGame from "@/pages/EndGame";
import HowToPlay from "@/pages/HowToPlay";
import AdminPage from "@/pages/AdminPage";
import AccountPage from "@/pages/AccountPage";
import AvatarOnboarding from "@/pages/AvatarOnboarding";
import JoinLocalGame from "@/pages/JoinLocalGame";
import TVPreview from "@/pages/TVPreview";
import TVGameplayDemo from "@/pages/TVGameplayDemo";
import ControllerPage from "@/pages/ControllerPage";

const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="retro-heading text-ink text-lg">Loading...</div>
      </div>
    );
  }
  
  if (!user) {
    return <Navigate to="/login" replace />;
  }
  
  // Redirect new users to avatar onboarding
  if (!user.avatar_setup_complete && window.location.pathname !== '/avatar-setup') {
    return <Navigate to="/avatar-setup" replace />;
  }
  
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="retro-heading text-ink text-lg">Loading...</div>
      </div>
    );
  }
  
  if (user) {
    return <Navigate to="/dashboard" replace />;
  }
  
  return children;
};

function App() {
  return (
    <AuthProvider>
      <GameProvider>
        <BrowserRouter>
          {/* Balatro-style CRT screen overlay */}
          <CRTScreen />
          <ScrollToTop />
          <Routes>
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="/login" element={
              <PublicRoute><LoginPage /></PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute><RegisterPage /></PublicRoute>
            } />
            <Route path="/dashboard" element={
              <ProtectedRoute><Dashboard /></ProtectedRoute>
            } />
            <Route path="/new-game" element={
              <ProtectedRoute><NewGameSetup /></ProtectedRoute>
            } />
            <Route path="/online" element={
              <ProtectedRoute><OnlineQueue /></ProtectedRoute>
            } />
            <Route path="/online/lobby/:matchId" element={
              <ProtectedRoute><OnlineLobby /></ProtectedRoute>
            } />
            <Route path="/coin-toss" element={
              <ProtectedRoute><CoinToss /></ProtectedRoute>
            } />
            <Route path="/game" element={
              <ProtectedRoute><GamePlay /></ProtectedRoute>
            } />
            <Route path="/inning-summary" element={
              <ProtectedRoute><InningSummary /></ProtectedRoute>
            } />
            <Route path="/end-game" element={
              <ProtectedRoute><EndGame /></ProtectedRoute>
            } />
            <Route path="/how-to-play" element={<HowToPlay />} />
            <Route path="/admin" element={
              <ProtectedRoute><AdminPage /></ProtectedRoute>
            } />
            <Route path="/account" element={
              <ProtectedRoute><AccountPage /></ProtectedRoute>
            } />
            <Route path="/avatar-setup" element={
              <ProtectedRoute><AvatarOnboarding /></ProtectedRoute>
            } />
            <Route path="/join-game" element={<JoinLocalGame />} />
            <Route path="/tv-preview" element={<TVPreview />} />
            <Route path="/tv-gameplay-demo" element={<TVGameplayDemo />} />
            <Route path="/controller/:code" element={<ControllerPage />} />
            <Route path="/controller" element={<ControllerPage />} />
          </Routes>
        </BrowserRouter>
        <Toaster position="top-center" />
      </GameProvider>
    </AuthProvider>
  );
}

export default App;
